import {useEffect, useRef, useState} from 'react';
import {NativeConnectionOperationSchema, nativeConnectionTargetCanApprove, nativeConnectionTargetCanSkip,
  type NativeConnectionOperation} from '../src/native-connection';
import {ErrorNotice} from './components';
import {errorDetails} from './lib/store';
import type {ErrorDetails} from './lib/types';

type Target = NativeConnectionOperation['targets'][number];
type Props = {
  connection: {sessionId: string; operation: Record<string, unknown>};
  busy: boolean;
  answer: (sessionId: string, operationId: string, result: Record<string, unknown>) => Promise<unknown>;
};
const labels: Record<Target['state'], string> = {
  pending: 'Waiting for your choice', initiated: 'Connecting', connected: 'Connected', skipped: 'Skipped',
  failed: 'Connection failed', expired: 'Connection request expired', not_connected: 'Not connected',
};

function secureAddress(value: string): string {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.username || url.password) throw new Error('The service supplied a connection link that does not use HTTPS or includes sign-in credentials.');
  return url.href;
}

function ConnectionLink({address, disabled}: {address: string; disabled: boolean}) {
  let href: string;
  try {href = secureAddress(address);} catch (error) {return <ErrorNotice title="The connection link could not be opened" error={errorDetails(error)}/>;}
  return disabled ? <span>Connect</span> : <a href={href} target="_blank" rel="noopener noreferrer">Connect</a>;
}

export function NativeConnection(props: Props) {
  let operation: NativeConnectionOperation;
  try {operation = NativeConnectionOperationSchema.parse(props.connection.operation);}
  catch (error) {return <ErrorNotice title="The connection request could not be shown" error={errorDetails(error)}/>;}
  return <ConnectionForm key={`${props.connection.sessionId}:${operation.op_id}`} {...props} operation={operation}/>;
}

function ConnectionForm({connection, operation, busy, answer}: Props & {operation: NativeConnectionOperation}) {
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>({});
  const [pending, setPending] = useState(false);
  const [submittedAt, setSubmittedAt] = useState<number | null>(null);
  const [failure, setFailure] = useState<ErrorDetails | null>(null);
  const lock = useRef(false), mounted = useRef(true);
  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
  const awaitingSnapshot = submittedAt !== null && operation.seq === submittedAt;
  useEffect(() => {if (!pending && !awaitingSnapshot) lock.current = false;}, [pending, awaitingSnapshot]);
  const disabled = busy || pending || awaitingSnapshot || operation.settled === true;
  function value(target: Target, name: string, initial: string | null | undefined): string {
    return drafts[target.name]?.[name] ?? initial ?? '';
  }
  function environment(target: Target): Record<string, string> {
    return Object.fromEntries((target.required_env ?? []).flatMap(field => {
      const supplied = value(target, field.name, field.default);
      return supplied ? [[field.name, supplied]] : [];
    }));
  }
  async function send(result: Record<string, unknown>, target?: Target) {
    if (disabled || lock.current) return;
    lock.current = true; setPending(true); setFailure(null);
    try {
      await answer(connection.sessionId, operation.op_id, result);
      if (mounted.current) {
        setSubmittedAt(operation.seq);
        if (target) setDrafts(current => {const next = {...current}; delete next[target.name]; return next;});
        else setDrafts({});
      }
    } catch (error) {
      if (mounted.current) setFailure(errorDetails(error));
      lock.current = false;
    } finally {if (mounted.current) setPending(false);}
  }
  return <section className="native-question" aria-label="Connect services">
    <h3>Connect services</h3>
    {operation.targets.map(target => {
      const approve = nativeConnectionTargetCanApprove(target), skip = nativeConnectionTargetCanSkip(target);
      const ready = (target.required_env ?? []).every(field => !field.required || value(target, field.name, field.default).trim().length > 0);
      return <fieldset key={target.name} disabled={disabled}>
        <legend>{target.display || target.name}</legend>
        <p aria-live="polite">{labels[target.state]}</p>
        {target.description && <p>{target.description}</p>}
        {target.detail && <p style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{target.detail}</p>}
        {target.instructions && <p style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{target.instructions}</p>}
        {target.hint && <p className="field-help">{target.hint}</p>}
        {target.scan && <p className="field-help">{target.scan.summary}</p>}
        {target.discovery_error && <ErrorNotice title="The service could not be read" error={{name: 'ConnectionDiscoveryError', message: target.discovery_error}}/>}
        {target.server_errors?.map(server => <ErrorNotice key={server.name} title={`${server.name} could not connect`} error={{name: 'ConnectionError', message: server.error}}/>)}
        {target.connect_url && skip && <p><ConnectionLink address={target.connect_url} disabled={disabled}/></p>}
        {approve && (target.required_env ?? []).map(field => <label className="field" key={field.name}>
          <span>{field.prompt || field.name}{field.required ? ' (required)' : ''}</span>
          <input type={field.secret ? 'password' : 'text'} autoComplete="off" required={field.required}
            value={value(target, field.name, field.default)} onChange={event => {
              const supplied = event.target.value;
              setDrafts(current => ({...current, [target.name]: {...current[target.name], [field.name]: supplied}}));
            }}/>
        </label>)}
        <div className="button-row">
          {approve && <button type="button" className="button" disabled={disabled || !ready}
            onClick={() => void send({targets: [{name: target.name, status: 'approved', env: environment(target)}]}, target)}>
            {target.action === 'install' ? 'Install' : target.action === 'enable' ? 'Enable' : 'Connect'}</button>}
          {skip && <button type="button" className="text-button" disabled={disabled}
            onClick={() => void send({targets: [{name: target.name, status: 'skipped'}]}, target)}>Skip</button>}
        </div>
      </fieldset>;
    })}
    <ErrorNotice error={failure}/>
    {(pending || awaitingSnapshot) && <p className="field-help" aria-live="polite">{pending ? 'Sending your response…' : 'Your response was sent. Waiting for the service status.'}</p>}
    {operation.settled !== true && <button type="button" className="button" disabled={disabled}
      onClick={() => void send({targets: [], settled_by: 'continue'})}>Continue without waiting</button>}
  </section>;
}
